class StockStatus extends HTMLElement {
  // Static constants - shared across all instances
  static MS_PER_HOUR = 3600000
  static MS_PER_MINUTE = 60000
  
  // Static time formatter - created once for all instances
  static timeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })

  constructor() {
    super()
    this.countdownInterval = null
    this.cutoffTime = null
    this.variantSelector = null
    this.mutationObserver = null
    this.retryTimeout = null
    this.updateTimeout = null // Store debounce timeout as instance property
    this.retryCount = 0
    this.maxRetries = 10
    
    // Cache default variant data to avoid repeated dataset access
    this.defaultVariantId = null
    this.defaultVariantAvailable = null
  }

  connectedCallback() {
    // Cache DOM elements once
    this.withStockEl = this.querySelector('[with-stock]')
    this.withoutStockEl = this.querySelector('[without-stock]')
    this.deliveryTimeEl = this.querySelector('.stock-status__with-stock__delivery-time')
    this.beforeCutoffEl = this.querySelector('.stock-status__before-cutoff')
    this.afterCutoffEl = this.querySelector('.stock-status__after-cutoff')
    
    // Cache default variant data
    this.defaultVariantId = this.dataset.defaultVariantId || null
    this.defaultVariantAvailable = this.dataset.defaultVariantAvailable === 'true'
    
    // Get cutoff time from element data attribute (format: "15:00" or "15:00:00")
    if (this.dataset.cutoffTime) {
      this.cutoffTime = this.parseCutoffTime(this.dataset.cutoffTime)
    } else {
      this.cutoffTime = { hours: 15, minutes: 0 } // Default to 15:00
    }

    // Wait for variant selector to be ready
    this.initVariantObserver()
  }

  initVariantObserver() {
    // Clear any pending retry
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout)
      this.retryTimeout = null
    }
    
    // Try to find variant selector, retry if not ready yet
    this.variantSelector = document.querySelector('variant-selector')
    
    if (this.variantSelector) {
      this.observeVariantChanges()
      // Use requestAnimationFrame for initial update - better performance
      requestAnimationFrame(() => {
        this.updateStockStatus()
      })
    } else {
      // Check if this is a product without variants (has default variant data)
      if (this.defaultVariantId) {
        // Product without variants - use default variant data
        this.updateStockStatus()
      } else {
        // Retry if variant selector not found yet (might still be loading)
        // But limit retries to avoid infinite loop
        if (this.retryCount < this.maxRetries) {
          this.retryCount++
          this.retryTimeout = setTimeout(() => {
            this.initVariantObserver()
          }, 100)
        } else {
          // After max retries, assume no variant selector exists
          // Hide stock status if we can't determine availability
          this.hideAllStockStatus()
        }
      }
    }
  }
  
  hideAllStockStatus() {
    if (this.withStockEl) this.withStockEl.style.display = 'none'
    if (this.withoutStockEl) this.withoutStockEl.style.display = 'none'
  }

  parseCutoffTime(timeString) {
    // Parse time string like "15:00" or "15:00:00"
    const parts = timeString.split(':').map(Number)
    return {
      hours: parts[0] || 15,
      minutes: parts[1] || 0
    }
  }

  observeVariantChanges() {
    // Clean up existing observer if any
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }
    
    // Clear any pending debounce timeout
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout)
      this.updateTimeout = null
    }
    
    // Watch for changes to active-variant attribute on variant items
    if (!this.variantSelector) return
    
    const variantItems = this.variantSelector.querySelectorAll('.variant-selector__item')
    if (!variantItems || variantItems.length === 0) return

    // Debounce updates to avoid excessive calls
    this.mutationObserver = new MutationObserver(() => {
      // Clear pending update
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout)
      }
      // Debounce: wait 50ms before updating (batches rapid changes)
      this.updateTimeout = setTimeout(() => {
        this.updateStockStatus()
        this.updateTimeout = null
      }, 50)
    })

    variantItems.forEach(item => {
      this.mutationObserver.observe(item, {
        attributes: true,
        attributeFilter: ['active-variant']
      })
    })
  }

  getActiveVariant() {
    // First, try to get variant from variant selector
    if (this.variantSelector) {
      // Cache the query result if we can
      const activeVariant = this.variantSelector.querySelector('.variant-selector__item[active-variant]')
      if (activeVariant) {
        return {
          element: activeVariant,
          available: activeVariant.dataset.variantAvailable === 'true',
          id: activeVariant.dataset.variantId
        }
      }
    }
    
    // If no variant selector or no active variant, check for default variant (products without variants)
    if (this.defaultVariantId) {
      return {
        element: null,
        available: this.defaultVariantAvailable,
        id: this.defaultVariantId
      }
    }
    
    return null
  }

  updateStockStatus() {
    const variant = this.getActiveVariant()
    
    if (!variant) {
      // No variant selected, hide both
      this.hideAllStockStatus()
      this.stopCountdown()
      return
    }

    // Batch DOM updates
    if (variant.available) {
      // In stock
      if (this.withStockEl) this.withStockEl.style.display = 'flex'
      if (this.withoutStockEl) this.withoutStockEl.style.display = 'none'
      this.updateDeliveryInfo()
      this.startCountdown()
    } else {
      // Out of stock
      if (this.withStockEl) this.withStockEl.style.display = 'none'
      if (this.withoutStockEl) this.withoutStockEl.style.display = 'flex'
      this.stopCountdown()
    }
  }

  getGermanyTime() {
    // Get current time in Germany timezone
    // Use static formatter for better performance
    const now = new Date()
    const parts = StockStatus.timeFormatter.formatToParts(now)
    
    // Optimize: use object with known keys instead of forEach
    const timeObj = {
      year: '',
      month: '',
      day: '',
      hour: '',
      minute: '',
      second: ''
    }
    
    // Faster iteration - only process what we need
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]
      if (part.type in timeObj) {
        timeObj[part.type] = part.value
      }
    }

    // Use Number() instead of parseInt for better performance
    return new Date(
      Number(timeObj.year),
      Number(timeObj.month) - 1,
      Number(timeObj.day),
      Number(timeObj.hour),
      Number(timeObj.minute),
      Number(timeObj.second)
    )
  }

  getTodayCutoff(germanyTime) {
    // Accept germanyTime as parameter to avoid recalculating
    const cutoff = new Date(germanyTime.getTime())
    cutoff.setHours(this.cutoffTime.hours, this.cutoffTime.minutes, 0, 0)
    return cutoff
  }

  updateDeliveryInfo() {
    // Calculate time once and reuse
    const germanyTime = this.getGermanyTime()
    const cutoff = this.getTodayCutoff(germanyTime)
    
    // Determine if before or after cutoff
    const isBeforeCutoff = germanyTime < cutoff
    
    // Show/hide appropriate delivery text
    if (this.beforeCutoffEl) {
      this.beforeCutoffEl.style.display = isBeforeCutoff ? 'inline' : 'none'
    }
    if (this.afterCutoffEl) {
      this.afterCutoffEl.style.display = isBeforeCutoff ? 'none' : 'inline'
    }

    // Update countdown display
    this.updateCountdownDisplay(germanyTime, cutoff)
  }

  updateCountdownDisplay(germanyTime, cutoff) {
    if (!this.deliveryTimeEl) return

    let timeUntilCutoff = cutoff - germanyTime
    let targetCutoff = cutoff

    // If cutoff has passed, calculate for tomorrow's cutoff
    if (timeUntilCutoff <= 0) {
      targetCutoff = new Date(cutoff.getTime())
      targetCutoff.setDate(targetCutoff.getDate() + 1)
      timeUntilCutoff = targetCutoff - germanyTime
    }

    // Use static constants
    const hours = Math.floor(timeUntilCutoff / StockStatus.MS_PER_HOUR)
    const minutes = Math.floor((timeUntilCutoff % StockStatus.MS_PER_HOUR) / StockStatus.MS_PER_MINUTE)

    // Format in German: "3 Std. 45 Min." - use array join for better performance
    const parts = []
    if (hours > 0) {
      parts.push(`${hours} Std.`)
    }
    if (minutes > 0) {
      parts.push(`${minutes} Min.`)
    }
    
    // Only update textContent if it changed (avoid unnecessary reflows)
    const timeText = parts.length > 0 ? parts.join(' ') : '0 Min.'
    if (this.deliveryTimeEl.textContent !== timeText) {
      this.deliveryTimeEl.textContent = timeText
    }
  }

  startCountdown() {
    this.stopCountdown() // Clear any existing interval
    
    // Update immediately using requestAnimationFrame for smoother updates
    requestAnimationFrame(() => {
      this.updateDeliveryInfo()
    })
    
    // Update every minute - use a more precise interval
    this.countdownInterval = setInterval(() => {
      this.updateDeliveryInfo()
    }, 60000) // Update every 60 seconds
  }

  stopCountdown() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }
  }

  disconnectedCallback() {
    // Clean up all resources
    this.stopCountdown()
    
    // Clear retry timeout
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout)
      this.retryTimeout = null
    }
    
    // Clear debounce timeout
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout)
      this.updateTimeout = null
    }
    
    // Disconnect mutation observer
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }
  }
}

customElements.define('stock-status', StockStatus)

